/**
 * TheWeeklyLoad — BigLoads "The Weekly Load" newsletter opt-in popup.
 *
 * A self-contained, client-only two-step popup that:
 *  - Step 1: hooks the visitor + asks their role (renter vs owner) via two big
 *    icon-circle role cards; role sets the AUDIENCE tag.
 *  - Step 2: role-adapted email capture → POSTs to /api/newsletter/subscribe
 *    with { email, audience, source }. Real validation + submit spinner +
 *    success state.
 *
 * Triggers (whichever fires first, once the visitor is eligible):
 *   - ~25s dwell time, OR
 *   - 50% scroll depth, OR
 *   - desktop exit-intent (mouse leaves toward the top of the viewport).
 *
 * Suppression (never shows):
 *   - logged-in users (Sharetribe auth-token cookie with a real user scope),
 *   - on checkout / auth / onboarding routes,
 *   - if the 60-day dismiss cookie is set,
 *   - if the long-lived "subscribed" cookie is set,
 *   - server-side (only mounted inside ClientApp, never ServerApp → no SSR/SEO
 *     impact).
 *
 * Zero external dependencies — plain React + a dedicated body portal + inline
 * styles so it can never collide with the marketplace CSS (same pattern as
 * LoadBot). Visual design matches the approved mock: light warm card, real
 * "THE WEEKLY LOAD" logo PNG, icon-in-circle role buttons, navy benefit
 * footer band, zero emoji (inline SVG line icons only).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';

import heroImg from '../../assets/newsletter/weekly-load-hero.jpg';
import logoImg from '../../assets/newsletter/weekly-load-logo.png';

const DISMISS_COOKIE = 'bl_wl_dismissed';
const SUBSCRIBED_COOKIE = 'bl_wl_subscribed';
const DISMISS_DAYS = 60;
const SUBSCRIBE_DAYS = 400; // "forever-ish" (max reliable cookie horizon ~13mo)

const DWELL_MS = 25000; // ~25s
const SCROLL_PCT = 0.5; // 50%

const SOURCE = 'weekly-load-popup';

// Routes where the popup must never appear (in-flow / high-intent surfaces).
// Matched as path prefixes against window.location.pathname.
const SUPPRESS_PREFIXES = [
  '/checkout',
  '/login',
  '/signup',
  '/recover-password',
  '/reset-password',
  '/verify-email',
  '/confirm',
  '/onboarding',
];

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const COLORS = {
  paper: '#F6F4EF',
  navy: '#0E1B2A',
  blue: '#2F7FE0',
  blueDark: '#1E5FB0',
  red: '#B02419',
  redDark: '#8E1D14',
  ink: '#1A1A1A',
  muted: '#5A6B7A',
  border: '#E3DFD6',
  green: '#2E9E6B',
};

/* ----------------------------- cookie helpers ----------------------------- */

const getCookie = name => {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name.replace(/([.$?*|{}()[\]\\/+^])/g, '\\$1') + '=([^;]*)')
  );
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name, value, days) => {
  if (typeof document === 'undefined') return;
  const d = new Date();
  d.setTime(d.getTime() + days * 24 * 60 * 60 * 1000);
  const secure = window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${d.toUTCString()}; path=/; SameSite=Lax${secure}`;
};

/* --------------------------- suppression helpers -------------------------- */

// Logged-in detection (client-only): the Sharetribe SDK's default cookie token
// store persists a token in a cookie named `st-<clientId>-token`. BUT that
// cookie exists for EVERY visitor — even anonymous ones — because the SDK first
// gets an anonymous "public-read" token. So the mere presence of the cookie
// does NOT mean the user is logged in.
//
// The token value is JSON `{access_token, scope, ...}` where `access_token` is a
// JWT whose payload carries the real scope. Anonymous = `public-read`; a signed-
// in user has a user scope (e.g. `user`, `trusted:user`). We decode the JWT
// payload and only treat the visitor as authenticated when the scope is NOT the
// anonymous public-read scope. No redux dependency (keeps this store-free like
// LoadBot); fails safe to "not logged in" so we never wrongly suppress.
const decodeJwtScope = accessToken => {
  try {
    const payload = accessToken.split('.')[1];
    if (!payload) return null;
    const b64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64 + '==='.slice((b64.length + 3) % 4);
    const json = JSON.parse(atob(pad));
    return json && json.scope ? String(json.scope) : null;
  } catch (e) {
    return null;
  }
};

const isLoggedIn = () => {
  if (typeof document === 'undefined') return false;
  try {
    const cookies = document.cookie.split(';');
    for (const c of cookies) {
      const [rawK, ...rest] = c.split('=');
      const k = rawK.trim();
      if (!/^st-.+-token$/.test(k)) continue;
      const raw = rest.join('=').trim();
      if (!raw || raw === 'null' || raw === 'undefined') continue;
      let parsed;
      try {
        parsed = JSON.parse(decodeURIComponent(raw));
      } catch (e) {
        continue;
      }
      const accessToken = parsed && parsed.access_token;
      if (!accessToken) continue;
      const scope = decodeJwtScope(accessToken);
      // Authenticated iff the scope exists and is NOT the anonymous scope.
      if (scope && scope !== 'public-read') return true;
    }
    return false;
  } catch (e) {
    return false;
  }
};

const isSuppressedRoute = () => {
  if (typeof window === 'undefined') return true;
  const p = window.location.pathname || '/';
  return SUPPRESS_PREFIXES.some(prefix => p === prefix || p.startsWith(prefix + '/') || p.startsWith(prefix));
};

// Overall eligibility gate, re-evaluated at mount.
const isEligible = () => {
  if (typeof window === 'undefined') return false;
  if (getCookie(DISMISS_COOKIE)) return false;
  if (getCookie(SUBSCRIBED_COOKIE)) return false;
  if (isLoggedIn()) return false;
  if (isSuppressedRoute()) return false;
  return true;
};

/* ------------------------------ inline icons ------------------------------ */
/* Clean line icons (no emoji). All accept a `color` + optional `size`. */

const TrailerIcon = ({ color = '#fff', size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="2" y="8" width="15" height="7" rx="1" />
    <path d="M17 12h5" />
    <circle cx="7" cy="17.5" r="1.8" />
    <circle cx="12.5" cy="17.5" r="1.8" />
  </svg>
);

const TagIcon = ({ color = '#fff', size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20.6 13.4L12 22l-8.6-8.6a2 2 0 0 1-.6-1.4V4a2 2 0 0 1 2-2h8a2 2 0 0 1 1.4.6l6.4 6.4a2 2 0 0 1 0 2.8z" transform="rotate(90 12 12)" />
    <circle cx="9" cy="9" r="1.6" fill={color} stroke="none" />
  </svg>
);

const MapPinIcon = ({ color = '#fff', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 21s7-5.6 7-11a7 7 0 1 0-14 0c0 5.4 7 11 7 11z" />
    <circle cx="12" cy="10" r="2.6" />
  </svg>
);

const BulbIcon = ({ color = '#fff', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18h6" />
    <path d="M10 21h4" />
    <path d="M12 3a6 6 0 0 0-4 10.5c.8.7 1.3 1.5 1.5 2.5h5c.2-1 .7-1.8 1.5-2.5A6 6 0 0 0 12 3z" />
  </svg>
);

const HandshakeIcon = ({ color = '#fff', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 9l4-3 5 2 5-2 6 3" />
    <path d="M11 8l-3.5 3.5a1.4 1.4 0 0 0 2 2L12 11l3 2.6a1.4 1.4 0 0 0 1.9-2L13.5 8.5" />
    <path d="M2 9v6l3 2M22 9v6l-3 2" />
  </svg>
);

const MoneyBagIcon = ({ color = '#fff', size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 6L8 3h8l-1.5 3" />
    <path d="M9.5 6h5c3 2.4 4.5 5.4 4.5 8.5 0 4-3 6.5-7 6.5s-7-2.5-7-6.5C5 11.4 6.5 8.4 9.5 6z" />
    <path d="M12 10.5v6M10 12h3.2a1.3 1.3 0 0 1 0 2.6H10.8a1.3 1.3 0 0 0 0 2.6H14" transform="scale(0.9) translate(1.3 1)" />
  </svg>
);

const EnvelopeIcon = ({ color = COLORS.blue, size = 84 }) => (
  <svg width={size} height={size * 0.72} viewBox="0 0 100 72" fill="none" aria-hidden="true">
    {/* motion lines */}
    <path d="M4 18h16M0 30h13M6 42h12" stroke={color} strokeWidth="3" strokeLinecap="round" />
    {/* envelope, slightly tilted */}
    <g transform="rotate(8 62 36)">
      <rect x="32" y="16" width="56" height="38" rx="4" fill="#fff" stroke={color} strokeWidth="3.5" />
      <path d="M34 19l26 20 26-20" stroke={color} strokeWidth="3.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
    </g>
  </svg>
);

const LockIcon = ({ color = '#fff', size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4" y="11" width="16" height="10" rx="2" />
    <path d="M8 11V7a4 4 0 0 1 8 0v4" />
  </svg>
);

const CheckCircleIcon = ({ color = COLORS.blue, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} aria-hidden="true">
    <circle cx="12" cy="12" r="11" />
    <path d="M7 12.5l3.2 3.2L17 9" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" fill="none" />
  </svg>
);

const TrailerGlyphSmall = ({ color = COLORS.blue, size = 26 }) => (
  <svg width={size} height={size * 0.6} viewBox="0 0 30 18" fill="none" aria-hidden="true">
    <rect x="1" y="3" width="19" height="8" rx="1.5" fill={color} />
    <path d="M20 7h9" stroke={color} strokeWidth="2.4" strokeLinecap="round" />
    <circle cx="7" cy="14" r="2.6" fill={color} />
    <circle cx="14" cy="14" r="2.6" fill={color} />
  </svg>
);

const CloseX = ({ color = COLORS.ink, size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.4" strokeLinecap="round" aria-hidden="true">
    <path d="M5 5l14 14M19 5L5 19" />
  </svg>
);

/* -------------------------------- component ------------------------------- */

const ROLE_COPY = {
  renter: {
    label: "I'm renting",
    sub: 'Find trailers + know what to look for',
    step2:
      "Nice. We'll send you the best trailers near you + tips so you rent smart.",
    // headline split for the mixed-color condensed rendering
    headParts: ["Nice. We'll send you the ", 'best trailers near you', ' + tips so you rent smart.'],
    pill: 'FOR RENTERS',
    benefits: [
      'New trailers listed near you',
      'What to check before you rent',
      'Towing & hauling tips that help',
    ],
  },
  owner: {
    label: 'I own / want to list',
    sub: 'Earn from your trailer + get more renters',
    step2:
      "Perfect. We'll show you how to turn an idle trailer into cash — and get renters calling you.",
    headParts: ["Perfect. We'll show you how to turn an ", 'idle trailer into cash', ' — and get renters calling you.'],
    pill: 'FOR OWNERS',
    benefits: [
      'What owners are earning',
      'Get more renters (we do the finding)',
      'Listing tips that get inquiries',
    ],
  },
};

const TRUST = [
  { Icon: MapPinIcon, text: 'New trailers near you' },
  { Icon: BulbIcon, text: 'Tips from real haulers' },
  { Icon: HandshakeIcon, text: 'Save money + avoid headaches' },
  { Icon: MoneyBagIcon, text: 'More renters. More earnings.' },
];

const TheWeeklyLoad = () => {
  const [portalEl, setPortalEl] = useState(null);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [step, setStep] = useState(1); // 1 = role choice, 2 = email capture
  const [role, setRole] = useState(null); // 'renter' | 'owner' | null(unknown)
  const [email, setEmail] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const firedRef = useRef(false);
  const inputRef = useRef(null);

  /* ---- owned body portal (zero layout footprint), like LoadBot ---- */
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    let el = document.getElementById('weekly-load-portal');
    let created = false;
    if (!el) {
      el = document.createElement('div');
      el.id = 'weekly-load-portal';
      el.style.position = 'fixed';
      el.style.top = '0';
      el.style.left = '0';
      el.style.width = '0';
      el.style.height = '0';
      el.style.zIndex = '2147483200';
      document.body.appendChild(el);
      created = true;
    }
    setPortalEl(el);
    return () => {
      if (created && el && el.parentNode) el.parentNode.removeChild(el);
    };
  }, []);

  /* ---- triggers: dwell timer + scroll depth + desktop exit-intent ----
     Re-armed on SPA route changes (popstate / pushState / replaceState) so a
     visitor who navigates from an eligible page onto a suppressed one — or the
     reverse — always gets a fresh, correct eligibility read. fire() also
     re-checks isEligible() at fire-time as a belt-and-suspenders guard. */
  const [routeTick, setRouteTick] = useState(0);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const bump = () => setRouteTick(t => t + 1);
    const wrap = fnName => {
      const orig = window.history[fnName];
      if (typeof orig !== 'function' || orig.__wlWrapped) return () => {};
      const wrapped = function (...args) {
        const r = orig.apply(this, args);
        window.dispatchEvent(new Event('wl:locationchange'));
        return r;
      };
      wrapped.__wlWrapped = true;
      window.history[fnName] = wrapped;
      return () => {
        if (window.history[fnName] === wrapped) window.history[fnName] = orig;
      };
    };
    const restorePush = wrap('pushState');
    const restoreReplace = wrap('replaceState');
    window.addEventListener('popstate', bump);
    window.addEventListener('wl:locationchange', bump);
    return () => {
      window.removeEventListener('popstate', bump);
      window.removeEventListener('wl:locationchange', bump);
      restorePush();
      restoreReplace();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (firedRef.current) return undefined; // already shown once this session
    if (!isEligible()) return undefined;

    const armedAt = Date.now();

    const fire = () => {
      if (firedRef.current) return;
      // Re-check eligibility at fire-time (route may have changed via SPA nav).
      if (!isEligible()) return;
      firedRef.current = true;
      setOpen(true);
    };

    const timer = window.setTimeout(fire, DWELL_MS);

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      if (window.scrollY / scrollable >= SCROLL_PCT) fire();
    };

    // Desktop exit-intent: mouse leaves through the top of the viewport.
    // Guard with a short min-dwell so devtools/tab-switch/immediate load noise
    // can't trip it in the first few seconds.
    const isDesktop =
      window.matchMedia && window.matchMedia('(pointer: fine)').matches && window.innerWidth >= 1024;
    const onMouseOut = e => {
      if (Date.now() - armedAt < 4000) return;
      if (!e.relatedTarget && !e.toElement && e.clientY <= 0) fire();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    if (isDesktop) document.addEventListener('mouseout', onMouseOut);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('scroll', onScroll);
      if (isDesktop) document.removeEventListener('mouseout', onMouseOut);
    };
  }, [routeTick]);

  /* ---- focus the email field when we reach step 2 ---- */
  useEffect(() => {
    if (open && step === 2 && inputRef.current) {
      const t = window.setTimeout(() => inputRef.current && inputRef.current.focus(), 60);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open, step]);

  const dismiss = useCallback(() => {
    // Only persist a dismiss cookie if they didn't subscribe (success sets its
    // own longer cookie). Animate out first.
    setClosing(true);
    window.setTimeout(() => {
      if (!done) setCookie(DISMISS_COOKIE, '1', DISMISS_DAYS);
      setOpen(false);
      setClosing(false);
    }, 180);
  }, [done]);

  /* ---- Esc to dismiss ---- */
  useEffect(() => {
    if (!open) return undefined;
    const onKey = e => {
      if (e.key === 'Escape') dismiss();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, dismiss]);

  const chooseRole = r => {
    setRole(r);
    setStep(2);
  };

  const submit = useCallback(
    async e => {
      if (e && e.preventDefault) e.preventDefault();
      setTouched(true);
      const clean = email.trim();
      if (!EMAIL_RE.test(clean)) {
        setError('Please enter a valid email.');
        return;
      }
      setError('');
      setSubmitting(true);
      const audience = role || 'unknown';

      // Shared event_id so the browser pixel Lead + the server CAPI Lead dedupe
      // to ONE Meta conversion. Read _fbp/_fbc cookies for advanced matching.
      const eventId =
        (typeof crypto !== 'undefined' && crypto.randomUUID && crypto.randomUUID()) ||
        'wl_' + Date.now() + '_' + Math.random().toString(36).slice(2);
      const readCookie = name => {
        try {
          const m = document.cookie.match(
            new RegExp('(?:^|;\\s*)' + name + '=([^;]+)')
          );
          return m ? decodeURIComponent(m[1]) : undefined;
        } catch (err) {
          return undefined;
        }
      };
      const fbp = readCookie('_fbp');
      const fbc = readCookie('_fbc');
      const pageUrl =
        typeof window !== 'undefined' && window.location
          ? window.location.href
          : undefined;

      // Fire the browser-side Meta pixel Lead (if fbq is present). Same eventId
      // as the server event => Meta counts it once. Best-effort, never blocks.
      try {
        if (typeof window !== 'undefined' && typeof window.fbq === 'function') {
          window.fbq(
            'track',
            'Lead',
            { content_name: 'weekly_load_newsletter', lead_type: audience },
            { eventID: eventId }
          );
        }
      } catch (err) {
        /* ignore pixel errors */
      }

      try {
        const res = await fetch('/api/newsletter/subscribe', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: clean,
            audience,
            source: SOURCE,
            list: 'bigloads-weekly-load',
            eventId,
            fbp,
            fbc,
            pageUrl,
          }),
        });
        // The endpoint captures locally even if Mailchimp is unwired/soft-errs
        // (200 or 202), so treat any 2xx as success from the visitor's view.
        if (res.ok || res.status === 202) {
          setDone(true);
          setCookie(SUBSCRIBED_COOKIE, audience, SUBSCRIBE_DAYS);
        } else if (res.status === 400) {
          setError('Please enter a valid email.');
        } else {
          setError("Hmm, that didn't go through. Try again in a moment.");
        }
      } catch (err) {
        setError("Couldn't reach the server. Give it another shot.");
      } finally {
        setSubmitting(false);
      }
    },
    [email, role]
  );

  if (typeof window === 'undefined' || !portalEl || !open) return null;

  const roleCopy = role ? ROLE_COPY[role] : null;
  const emailInvalid = touched && !EMAIL_RE.test(email.trim());
  const accent = role === 'owner' ? COLORS.red : COLORS.blue;
  const accentDark = role === 'owner' ? COLORS.redDark : COLORS.blueDark;

  return createPortal(
    <div
      style={{ ...styles.overlay, ...(closing ? styles.overlayClosing : {}) }}
      onMouseDown={e => {
        // Click on the backdrop (not the card) dismisses.
        if (e.target === e.currentTarget) dismiss();
      }}
      role="dialog"
      aria-modal="true"
      aria-label="The Weekly Load newsletter signup"
    >
      <div
        className="wl-card"
        style={{ ...styles.card, ...(closing ? styles.cardClosing : {}) }}
      >
        {/* Header: logo TL + close X TR */}
        <div style={styles.header}>
          <img src={logoImg} alt="The Weekly Load" className="wl-logo" style={styles.logo} />
          <button style={styles.closeBtn} onClick={dismiss} aria-label="Close">
            <CloseX />
          </button>
        </div>

        {done ? (
          <>
            <div className="wl-body" style={styles.body}>
              <div style={styles.successWrap}>
                <div style={styles.successCheck}>
                  <CheckCircleIcon color={COLORS.green} size={56} />
                </div>
                <h2 className="wl-head" style={styles.successHead}>You're on the list</h2>
                <p style={styles.sub}>
                  Look out for your first <strong>Weekly Load</strong> soon — trailer
                  tips, deals, and listings near you.
                </p>
                <button
                  style={{ ...styles.ctaBtn, background: accent, marginTop: '6px' }}
                  onClick={dismiss}
                  onMouseEnter={hoverOn}
                  onMouseLeave={hoverOff}
                >
                  DONE
                </button>
              </div>
            </div>
            <div style={styles.footerBandSlim}>
              <span style={styles.footerLockWrap}>
                <LockIcon />
                <span style={styles.footerFine}>One email a week. Unsubscribe anytime.</span>
              </span>
              <a href="/privacy-policy" style={styles.footerLink}>Privacy Policy</a>
            </div>
          </>
        ) : step === 1 ? (
          <>
            <div className="wl-body wl-body1" style={styles.body1}>
              <h2 className="wl-head wl-head1" style={styles.head1}>
                <span style={{ color: COLORS.ink }}>GET THE</span>
                <br />
                <span style={{ color: COLORS.red }}>WEEKLY LOAD</span>
              </h2>
              <div style={styles.dividerRow}>
                <span style={styles.dividerLine} />
                <TrailerGlyphSmall />
              </div>
              <p style={styles.subCenter}>
                The best trailer tips, deals, and listings near you — once a week, no spam.
              </p>
            </div>

            {/* Hero photo zone with the role cards riding on top */}
            <div className="wl-heroZone" style={styles.heroZone}>
              <div style={styles.heroFade} />
              <div className="wl-roles" style={styles.roleWrap}>
                <button
                  type="button"
                  className="wl-roleCard"
                  style={{ ...styles.roleCard, borderColor: COLORS.blue }}
                  onClick={() => chooseRole('renter')}
                  onMouseEnter={hoverOn}
                  onMouseLeave={hoverOff}
                >
                  <span className="wl-roleCircle" style={{ ...styles.roleCircle, background: COLORS.blue }}>
                    <TrailerIcon size={30} />
                  </span>
                  <span className="wl-roleLabel" style={{ ...styles.roleLabel, color: COLORS.blue }}>
                    I'M RENTING
                  </span>
                  <span style={styles.roleSub}>{ROLE_COPY.renter.sub}</span>
                </button>
                <button
                  type="button"
                  className="wl-roleCard"
                  style={{ ...styles.roleCard, borderColor: COLORS.red }}
                  onClick={() => chooseRole('owner')}
                  onMouseEnter={hoverOn}
                  onMouseLeave={hoverOff}
                >
                  <span className="wl-roleCircle" style={{ ...styles.roleCircle, background: COLORS.red }}>
                    <TagIcon size={28} />
                  </span>
                  <span className="wl-roleLabel" style={{ ...styles.roleLabel, color: COLORS.red }}>
                    I OWN / WANT TO LIST
                  </span>
                  <span style={styles.roleSub}>{ROLE_COPY.owner.sub}</span>
                </button>
              </div>
            </div>

            {/* Navy benefit footer band */}
            <div className="wl-trust" style={styles.trustBand}>
              {TRUST.map(({ Icon, text }) => (
                <div style={styles.trustItem} key={text}>
                  <Icon />
                  <span style={styles.trustText}>{text}</span>
                </div>
              ))}
            </div>
          </>
        ) : (
          <>
            <div className="wl-body" style={styles.body}>
              <div style={styles.pillRow}>
                <button
                  type="button"
                  style={styles.backBtn}
                  onClick={() => {
                    setStep(1);
                    setError('');
                  }}
                  aria-label="Back"
                >
                  ‹ Back
                </button>
                <span style={{ ...styles.pill, background: accent }}>
                  {role === 'owner' ? <TagIcon size={15} /> : <TrailerIcon size={16} />}
                  {roleCopy ? roleCopy.pill : ''}
                </span>
              </div>

              <div className="wl-headRow" style={styles.headRow}>
                <h2 className="wl-head wl-head2" style={styles.head2}>
                  {roleCopy ? (
                    <>
                      {roleCopy.headParts[0]}
                      <span style={{ color: accent }}>{roleCopy.headParts[1]}</span>
                      {roleCopy.headParts[2]}
                    </>
                  ) : null}
                </h2>
                <div className="wl-envelope" style={styles.envelopeWrap}>
                  <EnvelopeIcon color={accent} />
                </div>
              </div>

              <form className="wl-form" style={styles.form} onSubmit={submit} noValidate>
                <input
                  ref={inputRef}
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  placeholder="Enter your email address"
                  value={email}
                  disabled={submitting}
                  onChange={e => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  onBlur={() => setTouched(true)}
                  style={{
                    ...styles.input,
                    ...(emailInvalid || error ? styles.inputError : {}),
                  }}
                  aria-label="Email address"
                  aria-invalid={emailInvalid || !!error}
                />
                <button
                  type="submit"
                  style={{ ...styles.ctaBtn, background: submitting ? accentDark : accent }}
                  disabled={submitting}
                  onMouseEnter={hoverOn}
                  onMouseLeave={hoverOff}
                >
                  {submitting ? (
                    <span style={styles.spinnerWrap}>
                      <span style={styles.spinner} />
                      SENDING…
                    </span>
                  ) : (
                    'SEND IT MY WAY'
                  )}
                </button>
              </form>
              {error ? <div style={styles.errorText}>{error}</div> : null}

              <div className="wl-benefits" style={styles.benefitRows}>
                {(roleCopy ? roleCopy.benefits : []).map(b => (
                  <div style={styles.benefitItem} key={b}>
                    <CheckCircleIcon color={accent} />
                    <span style={styles.benefitText}>{b}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Navy fine-print footer band */}
            <div style={styles.footerBandSlim}>
              <span style={styles.footerLockWrap}>
                <LockIcon />
                <span style={styles.footerFine}>One email a week. Unsubscribe anytime.</span>
              </span>
              <a href="/privacy-policy" style={styles.footerLink}>Privacy Policy</a>
            </div>
          </>
        )}
      </div>

      <style>{keyframes}</style>
    </div>,
    portalEl
  );
};

/* --------------------------- hover micro-states --------------------------- */
const hoverOn = e => {
  e.currentTarget.style.transform = 'translateY(-1px)';
  e.currentTarget.style.filter = 'brightness(1.05)';
};
const hoverOff = e => {
  e.currentTarget.style.transform = 'translateY(0)';
  e.currentTarget.style.filter = 'none';
};

/* -------------------------------- keyframes ------------------------------- */
const keyframes = `
@import url('https://fonts.googleapis.com/css2?family=Oswald:wght@600;700&display=swap');
@keyframes wlFadeIn { from { opacity: 0; } to { opacity: 1; } }
@keyframes wlFadeOut { from { opacity: 1; } to { opacity: 0; } }
@keyframes wlPopIn { from { transform: translateY(16px) scale(0.97); opacity: 0; } to { transform: translateY(0) scale(1); opacity: 1; } }
@keyframes wlPopOut { from { transform: translateY(0) scale(1); opacity: 1; } to { transform: translateY(8px) scale(0.98); opacity: 0; } }
@keyframes wlSpin { to { transform: rotate(360deg); } }
@keyframes wlCheckPop { 0% { transform: scale(0.4); opacity: 0; } 60% { transform: scale(1.12); } 100% { transform: scale(1); opacity: 1; } }
@media (max-width: 560px) {
  /* Shrink the whole card on phones so it fits smaller viewports (and just
     feels lighter). Paired with the sticky header + top-pin safety net below
     so it can never trap a user even on the very smallest screens. */
  #weekly-load-portal .wl-card { width: min(400px, 100%) !important; border-radius: 16px !important; }
  #weekly-load-portal .wl-logo { width: 116px !important; }
  #weekly-load-portal .wl-body { padding: 12px 18px 16px !important; }
  #weekly-load-portal .wl-body1 { padding: 4px 18px 8px !important; }
  #weekly-load-portal .wl-head1 { font-size: 34px !important; }
  #weekly-load-portal .wl-head2 { font-size: 20px !important; }
  #weekly-load-portal .wl-roles { grid-template-columns: 1fr 1fr !important; gap: 10px !important; }
  #weekly-load-portal .wl-roleLabel { font-size: 15px !important; }
  #weekly-load-portal .wl-roleCard { padding: 12px 8px 12px !important; gap: 6px !important; }
  #weekly-load-portal .wl-roleCircle { width: 46px !important; height: 46px !important; }
  #weekly-load-portal .wl-heroZone { padding: 26px 14px 16px !important; }
  #weekly-load-portal .wl-trust { grid-template-columns: 1fr 1fr !important; gap: 6px !important; padding: 12px 14px 14px !important; }
  #weekly-load-portal .wl-envelope { display: none !important; }
  #weekly-load-portal .wl-headRow { display: block !important; }
  #weekly-load-portal .wl-form { flex-direction: column !important; }
  #weekly-load-portal .wl-benefits { grid-template-columns: 1fr !important; }
}
/* Very small / older phones: stack the two role cards so they never get cramped. */
@media (max-width: 360px) {
  #weekly-load-portal .wl-roles { grid-template-columns: 1fr !important; }
}
`;

/* --------------------------------- styles --------------------------------- */
const CONDENSED =
  "'Oswald', 'Archivo Narrow', 'Arial Narrow', 'Franklin Gothic Medium', Impact, system-ui, sans-serif";

const styles = {
  overlay: {
    position: 'fixed',
    inset: 0,
    zIndex: 2147483201,
    background: 'rgba(6, 12, 20, 0.66)',
    backdropFilter: 'blur(2px)',
    WebkitBackdropFilter: 'blur(2px)',
    display: 'flex',
    // flex-start (not center) so that when the card is TALLER than the viewport
    // it pins to the top of the scroll area and its header/close-X is always
    // reachable. When the card is SHORTER, `margin: auto` on the card still
    // vertically centers it. Centering a taller-than-viewport flex child with
    // overflow:auto clips the top out of reach — that was the trapped-user bug.
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '18px',
    fontFamily: "'Inter', system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
    animation: 'wlFadeIn 0.2s ease-out',
    pointerEvents: 'auto',
    overflowY: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  overlayClosing: { animation: 'wlFadeOut 0.18s ease-in forwards' },
  card: {
    position: 'relative',
    width: 'min(480px, 100%)',
    // Vertically center when it fits; pin to top (via flex-start overlay) when
    // it doesn't. `margin: auto` inside a flex-start column does the centering.
    margin: 'auto',
    background: COLORS.paper,
    borderRadius: '20px',
    overflow: 'hidden',
    boxShadow: '0 24px 70px rgba(0,0,0,0.45)',
    border: '1px solid rgba(0,0,0,0.08)',
    animation: 'wlPopIn 0.24s cubic-bezier(0.18, 0.9, 0.32, 1.2)',
    color: COLORS.ink,
  },
  cardClosing: { animation: 'wlPopOut 0.18s ease-in forwards' },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    padding: '18px 18px 0 22px',
  },
  logo: { width: '160px', height: 'auto', display: 'block' },
  closeBtn: {
    // Solid, high-contrast circular tap target so the close affordance is
    // never lost against the light paper header (and always ≥40px for touch).
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '40px',
    height: '40px',
    flex: '0 0 auto',
    borderRadius: '50%',
    background: 'rgba(14,27,42,0.06)',
    border: '1px solid rgba(14,27,42,0.10)',
    padding: '0',
    cursor: 'pointer',
    lineHeight: 0,
    WebkitTapHighlightColor: 'transparent',
  },
  body: { padding: '14px 24px 22px' },
  body1: { padding: '10px 24px 14px', textAlign: 'center' },

  /* Step 1 */
  head1: {
    margin: '4px 0 8px',
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '46px',
    lineHeight: 0.98,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  dividerRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    margin: '2px 0 10px',
  },
  dividerLine: {
    display: 'inline-block',
    width: '150px',
    height: '3px',
    borderRadius: '2px',
    background: COLORS.blue,
  },
  subCenter: {
    margin: '0 auto',
    maxWidth: '340px',
    fontSize: '14.5px',
    lineHeight: 1.5,
    color: COLORS.ink,
    fontWeight: 600,
  },
  heroZone: {
    position: 'relative',
    backgroundImage: `url(${heroImg})`,
    backgroundSize: 'cover',
    backgroundPosition: 'center 55%',
    padding: '38px 20px 22px',
  },
  heroFade: {
    position: 'absolute',
    inset: 0,
    background: `linear-gradient(180deg, ${COLORS.paper} 0%, rgba(246,244,239,0.4) 30%, rgba(246,244,239,0) 60%)`,
  },
  roleWrap: {
    position: 'relative',
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '14px',
  },
  roleCard: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '18px 12px 16px',
    borderRadius: '12px',
    border: '2px solid',
    background: '#fff',
    cursor: 'pointer',
    textAlign: 'center',
    transition: 'transform 0.12s ease, filter 0.12s ease, box-shadow 0.12s ease',
    boxShadow: '0 8px 22px rgba(0,0,0,0.25)',
    fontFamily: 'inherit',
  },
  roleCircle: {
    width: '58px',
    height: '58px',
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '2px',
  },
  roleLabel: {
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '19px',
    lineHeight: 1.05,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
  },
  roleSub: {
    fontSize: '13px',
    lineHeight: 1.35,
    color: COLORS.ink,
    fontWeight: 600,
    maxWidth: '160px',
  },
  trustBand: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr 1fr',
    gap: '8px',
    padding: '16px 16px 18px',
    background: COLORS.navy,
  },
  trustItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
    gap: '7px',
  },
  trustText: { fontSize: '12px', lineHeight: 1.3, color: '#fff', fontWeight: 500 },

  /* Step 2 */
  pillRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
  },
  backBtn: {
    background: 'transparent',
    border: 'none',
    color: COLORS.muted,
    fontSize: '13px',
    fontWeight: 700,
    cursor: 'pointer',
    padding: '0',
  },
  pill: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '7px',
    color: '#fff',
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '13px',
    letterSpacing: '1px',
    textTransform: 'uppercase',
    padding: '6px 14px',
    borderRadius: '999px',
  },
  headRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '14px',
  },
  head2: {
    margin: 0,
    flex: 1,
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '22px',
    lineHeight: 1.28,
    color: COLORS.ink,
  },
  envelopeWrap: { flex: '0 0 auto', paddingTop: '2px' },
  form: { display: 'flex', gap: '10px' },
  input: {
    flex: '1 1 auto',
    minWidth: 0,
    boxSizing: 'border-box',
    background: '#fff',
    color: COLORS.ink,
    border: `1px solid #C9C4B8`,
    borderRadius: '8px',
    padding: '14px 14px',
    fontSize: '15px',
    fontFamily: 'inherit',
    outline: 'none',
  },
  inputError: { border: `1px solid ${COLORS.red}`, background: 'rgba(176,36,25,0.05)' },
  errorText: { color: COLORS.red, fontSize: '12.5px', margin: '8px 2px 0', fontWeight: 600 },
  ctaBtn: {
    flex: '0 0 auto',
    padding: '14px 20px',
    borderRadius: '8px',
    border: 'none',
    color: '#fff',
    fontFamily: CONDENSED,
    fontSize: '15px',
    fontWeight: 700,
    letterSpacing: '0.8px',
    textTransform: 'uppercase',
    cursor: 'pointer',
    transition: 'transform 0.12s ease, filter 0.12s ease',
    boxShadow: '0 6px 16px rgba(0,0,0,0.18)',
    whiteSpace: 'nowrap',
  },
  spinnerWrap: { display: 'inline-flex', alignItems: 'center', gap: '9px' },
  spinner: {
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    border: '2px solid rgba(255,255,255,0.4)',
    borderTopColor: '#fff',
    display: 'inline-block',
    animation: 'wlSpin 0.7s linear infinite',
  },
  benefitRows: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr 1fr',
    gap: '10px',
    marginTop: '16px',
  },
  benefitItem: { display: 'flex', alignItems: 'flex-start', gap: '8px' },
  benefitText: { fontSize: '12.5px', lineHeight: 1.35, color: COLORS.ink, fontWeight: 600 },
  footerBandSlim: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '10px',
    flexWrap: 'wrap',
    padding: '11px 18px',
    background: COLORS.navy,
  },
  footerLockWrap: { display: 'inline-flex', alignItems: 'center', gap: '8px' },
  footerFine: { fontSize: '12px', color: '#fff' },
  footerLink: {
    fontSize: '12px',
    color: '#fff',
    textDecoration: 'underline',
    whiteSpace: 'nowrap',
  },

  /* Success */
  successWrap: { textAlign: 'center', padding: '10px 4px 8px' },
  successCheck: {
    display: 'flex',
    justifyContent: 'center',
    margin: '2px auto 14px',
    animation: 'wlCheckPop 0.4s cubic-bezier(0.18, 0.9, 0.32, 1.4)',
  },
  successHead: {
    margin: '0 0 8px',
    fontFamily: CONDENSED,
    fontWeight: 700,
    fontSize: '30px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: COLORS.ink,
  },
  sub: {
    margin: '0 0 16px',
    fontSize: '14.5px',
    lineHeight: 1.5,
    color: COLORS.muted,
  },
};

export default TheWeeklyLoad;
